import axios from 'axios';
// Dynamically import authApi where needed to avoid circular dependency
import store from '@/store'; // ✅ this is your default export
const backend_url = process.env.VUE_APP_BACKEND_URL;

store.commit('setApiLink', backend_url);

const apiClient = axios.create({
    baseURL: backend_url,
    timeout: 30000,
    withCredentials: true, // Required for secure cookies
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',
});

// List of guest routes
const guestRoutes = [
    '/auth/login',
    '/auth/register',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/verify-email',
    '/system/ping',
    '/users/invitation/validate',
    '/users/invitation/complete',
    '/sanctum/csrf-cookie', // Added for Sanctum
    '/error-reports/submit'
];

// Array of guest route patterns
const guestRouteWithParamsPatterns = [
    /^\/auth\/customer-login(\?.*)?$/
];

// Function to read a cookie value
const getCookie = (name) => {
    if (typeof document === 'undefined') return null;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
    return null;
};

// Session activity tracking
let lastRefreshTime = Date.now();
let sessionRefreshInProgress = false;

// Request Interceptor
apiClient.interceptors.request.use(async config => {
    const isFormData = config.data instanceof FormData;

    // Manually inject CSRF token from cookie for all requests
    // This is more reliable than Axios's built-in xsrf handling across subdomains
    if (typeof document !== 'undefined') {
        const token = getCookie('XSRF-TOKEN');
        if (token) {
            config.headers['X-XSRF-TOKEN'] = token;
        }

        // Add Bearer Token if available (Fixes Tracking Prevention issues)
        const authToken = localStorage.getItem('authToken');
        if (authToken && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${authToken}`;
        }
        
        // Ensure staff_token cookie is synced for backend web route bypass
        if (authToken && getCookie('staff_token') !== authToken) {
            document.cookie = `staff_token=${authToken}; path=/; max-age=86400; SameSite=Lax`;
        }
    }

    // Auto-refresh session if user is active and session is approaching expiration
    // Only for authenticated requests (not guest routes)
    const isGuestRoute = guestRoutes.some(route => config.url?.includes(route)) ||
        guestRouteWithParamsPatterns.some(pattern => pattern.test(config.url || ''));

    if (!isGuestRoute && store.state.isAuthenticated && !sessionRefreshInProgress) {
        const storedStartTime = parseInt(localStorage.getItem('auth_start_time') || '0', 10);
        const storedTtl = parseInt(localStorage.getItem('auth_ttl') || '0', 10) || (480 * 60 * 1000);
        
        // If start_time is recorded, check overall auth session age
        if (storedStartTime > 0 && (Date.now() - storedStartTime) > storedTtl) {
            import('@/api').then(({ authApi }) => authApi.logoutAndClear(store));
            const error = new Error('SILENT_EXPIRED');
            error.isSilent = true;
            return Promise.reject(error);
        }

        const timeSinceLastRefresh = Date.now() - lastRefreshTime;
        const refreshThreshold = 30 * 60 * 1000; // Refresh session cookie if within 30 minutes of refresh interval
        
        if (timeSinceLastRefresh > (storedTtl - refreshThreshold)) {
            sessionRefreshInProgress = true;
            try {
                // Make a lightweight request to refresh the session
                const baseUrl = (backend_url || '').replace(/\/api\/?$/, '');
                await axios.get(`${baseUrl}/sanctum/csrf-cookie?t=${Date.now()}`, {
                    withCredentials: true
                });
                lastRefreshTime = Date.now();
            } catch (error) {
                // Ignore refresh failure
            } finally {
                sessionRefreshInProgress = false;
            }
        }
    }

    if (config.url && (config.url.includes('/preview') || config.url.includes('/download'))) {
        config.headers['Accept'] = '*/*';
        if (config.url.includes('/download')) {
            config.responseType = 'blob';
        }
    } else if (config.url && (config.url.startsWith('/storage/') || config.url.startsWith('/attendance/'))) {
        config.headers['Accept'] = 'application/json';
        if (!isFormData) {
            config.headers['Content-Type'] = 'application/json';
        } else {
            delete config.headers['Content-Type'];
        }
    } else {
        config.headers['Accept'] = 'application/json';
        if (isFormData) {
            delete config.headers['Content-Type'];
        } else {
            config.headers['Content-Type'] = 'application/json';
        }
    }

    return config;
}, error => Promise.reject(error));

// Response Interceptor
let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
}


apiClient.interceptors.response.use(
    response => {
        // Check for version mismatch in headers
        const serverVersion = response.headers['x-app-version'];
        const clientVersion = process.env.VUE_APP_VERSION;

        if (serverVersion && clientVersion && serverVersion !== clientVersion) {
            // New version detected on server!
            // Dispatch event to App.vue to trigger SW update check
            window.dispatchEvent(new CustomEvent('sw-check-for-updates'));
        }

        // Check for hard-refresh cache clear header from backend
        const clearCacheHeader = response.headers['x-vect-clear-cache'];
        if (clearCacheHeader === '1' || clearCacheHeader === 1) {
            (async () => {
                try {
                    const { clearAllCache } = await import('@/utils/cacheService.js');
                    await clearAllCache();
                } catch (err) {
                    // Ignore dynamic import failure
                }
            })();
        }

        return response;
    },
    async error => {
        if (error.isSilent) {
            return Promise.reject(error);
        }

        const originalRequest = error.config;

        // Suppress toast for specific noisy endpoints (Selfies/Previews/Details)
        const isBackgroundRequest =
            originalRequest?.url?.includes('/storage/preview') ||
            originalRequest?.url?.includes('/storage/details') ||
            originalRequest?.url?.includes('/storage/share') ||
            originalRequest?.url?.includes('/storage/comment');

        const isOffline = typeof window !== 'undefined' && navigator && navigator.onLine === false;

        if (!error.response) {
            // ✅ Skip interceptor if the request was to /auth/logout
            if (originalRequest?.url?.includes('/auth/logout')) {
                return Promise.reject(error);
            }
            if (error.code === "ERR_NETWORK" && !window.location.pathname.includes('/signin')) {
                // authApi.logoutAndClear(store); // Don't force logout on network disconnect
            }
            error.message = 'No internet / Browser hang / Try icognito mode.';
        } else if (error.response.status === 401) {
            // Avoid looping if we are already on signin or trying to login/logout/refresh
            const isAuthRequest = originalRequest?.url?.includes('/auth/login') || originalRequest?.url?.includes('/auth/logout') || originalRequest?.url?.includes('/auth/refresh');
            
            if (!isAuthRequest && !originalRequest._retry) {
                originalRequest._retry = true;
                const refreshToken = localStorage.getItem('refreshToken');
                
                if (refreshToken) {
                    try {
                        const { default: authApi } = await import('@/api/auth');
                        const response = await apiClient.post('/auth/refresh', { refresh_token: refreshToken });
                        if (response.status === 200) {
                            const newAuthToken = response.data.data.access_token;
                            localStorage.setItem('authToken', newAuthToken);
                            // Update the failed request with the new token
                            originalRequest.headers.Authorization = `Bearer ${newAuthToken}`;
                            return apiClient(originalRequest);
                        }
                    } catch (refreshError) {
                        // Refresh failed, fall through to logout
                    }
                }
            }

            if (!isAuthRequest && !window.location.pathname.includes('/signin')) {
                import('@/api').then(({ authApi }) => authApi.default ? authApi.default.logoutAndClear(store) : authApi.logoutAndClear(store));
            }
            error.message = 'Session expired: Please log in again.';
        } else if (error.response.status === 429) {
            // Too Many Requests
            error.message = 'Too many requests. Please slow down and try again in a minute.';
            if (typeof window !== 'undefined' && !isOffline) {
                store.dispatch('triggerToast', {
                    message: error.message,
                    type: 'warning',
                    duration: 5000,
                    position: 'top-right',
                });
            }
        } else if (error.response.status === 419) {
            // CSRF mismatch - usually solved by fresh token or refresh
            if (!originalRequest?.url?.includes('/auth/logout')) {
                import('@/api').then(({ authApi }) => authApi.logoutAndClear(store));
            }
            error.message = 'Security token expired. Please log in again.';
        } else if (error.response.status === 404) {
            // Suppress toast for 404s, let the components handle redirects natively
        } else if (error.response.status === 422) {
            // Unprocessable Entity (Validation Error)
            let errorMessage = error.response?.data?.message || 'Validation failed.';
            if (error.response?.data?.errors) {
                const errorList = error.response.data.errors;
                const messages = [];
                Object.keys(errorList).forEach(key => {
                    const fieldErrors = errorList[key];
                    if (Array.isArray(fieldErrors)) {
                        messages.push(...fieldErrors);
                    } else if (typeof fieldErrors === 'string') {
                        messages.push(fieldErrors);
                    }
                });
                if (messages.length > 0) {
                    errorMessage = messages.join('\n');
                }
            }
            error.message = errorMessage;
            if (typeof window !== 'undefined' && !originalRequest?.url?.includes('/auth/login') && !originalRequest?.url?.includes('/user/profile') && !isBackgroundRequest && !isOffline) {
                store.dispatch('triggerToast', {
                    message: errorMessage,
                    type: 'error',
                    duration: 5000,
                    position: 'top-right',
                });
            }
        } else {
            // Show danger toast notification for other errors
            // SKIP TOASTER for background/noisy requests or login
            if (typeof window !== 'undefined' && !originalRequest?.url?.includes('/auth/login') && !originalRequest?.url?.includes('/user/profile') && !isBackgroundRequest && !isOffline) {
                const errorMessage = (store.state.appMode === "development" ? error.response?.data?.message || error.message : error.message) || 'Something went wrong.';
                store.dispatch('triggerToast', {
                    message: errorMessage,
                    type: 'error',
                    duration: 5000,
                    position: 'top-right',
                });
            }
        }
        return Promise.reject(error);
    }
);

export default apiClient;
